import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { discordStartUrl, joinWithProvider, MIN_PASSWORD_LENGTH, steamStartUrl } from '../api/auth';
import { ApiError } from '../api/http';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { useMeta } from './MetaContext';
import { nextQuery } from './returnPath';
import { ProviderButton } from './ProviderButton';
import { Wordmark } from './Wordmark';

/**
 * Where to go after signing in: a path of this site, or an HTTPS address on
 * the same parent domain (the panel host, which shares the session); the
 * platform applies the same rule to the Steam and Discord round trips.
 */
function safeNext(next: string | null): string | null {
  if (!next) return null;
  if (next.startsWith('/')) return next.startsWith('//') || next.startsWith('/api/') ? null : next;
  try {
    const url = new URL(next);
    const parent = window.location.hostname.split('.').slice(-2).join('.');
    const onDomain = url.hostname === parent || url.hostname.endsWith(`.${parent}`);
    return url.protocol === 'https:' && onDomain ? next : null;
  } catch {
    return null;
  }
}

/**
 * A username to propose from the name a provider or the game knows somebody
 * by: what the platform accepts (lower case, digits, dot, hyphen, underscore;
 * 3 to 32), or nothing when too little is left of the name. Only a
 * suggestion: the platform checks whatever is sent, and says so if it is
 * taken.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function handleFrom(name: string | null): string {
  const slug = (name ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '')
    .slice(0, 28)
    .replace(/\.+$/, '');
  return slug.length >= 3 ? slug : '';
}

/**
 * The sign-in page: three equal doors, Steam, Discord and the SALC account
 * (UI design §5.10; the owner, 2026-09-27). The third opens the password
 * form under them. A bounce from a provider ("pending": it confirmed who you
 * are, but no account has it verified) opens the form at once, since the
 * password now completes the link.
 *
 * A bounce that says "new" is somebody the group already knows (the game has
 * seen that Steam id play, or that Discord account is in the group's server):
 * the page offers them an account with no invitation, and asks only what to
 * call it. Who they are stays in the platform's session; `who` in the address
 * is a name to greet with and to prefill, never what the account is made from.
 */
export function SignIn() {
  const { meta } = useMeta();
  const { me, refresh, signIn } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const steamNotice = search.get('steam');
  const discordNotice = search.get('discord');
  const who = search.get('who');
  const next = safeNext(search.get('next'));
  const providers = meta.features.steam || meta.features.discord;
  // "unsure": Discord confirmed who they are and would not say whether they are in the group's
  // server. Everything a pending bounce offers still holds, and the page says trying again may help.
  const unsure = discordNotice === 'unsure';
  const pending = steamNotice === 'pending' || discordNotice === 'pending' || unsure;
  // A provider confirmed someone the group already knows and no account holds: theirs is offered.
  const offered: 'STEAM' | 'DISCORD' | null =
    steamNotice === 'new' ? 'STEAM' : discordNotice === 'new' ? 'DISCORD' : null;
  // The doors are known only once /api/meta has answered; with none, or after a pending bounce, the
  // form is open. Not under an offer: two forms asking for a username would be one too many, and the
  // third door still opens this one for whoever already had an account.
  const [passwordOpen, setPasswordOpen] = useState(false);
  const formShown = !providers || pending || passwordOpen;
  const [handle, setHandle] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [joinHandle, setJoinHandle] = useState(() => handleFrom(who));
  const [joinName, setJoinName] = useState(who ?? '');
  const [joinPassword, setJoinPassword] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);

  async function join(event: FormEvent) {
    event.preventDefault();
    if (!offered) return;
    setBusy(true);
    setJoinError(null);
    try {
      await joinWithProvider(offered, {
        handle: joinHandle,
        displayName: joinName,
        password: joinPassword,
      });
      await refresh();
      navigate(next && next.startsWith('/') ? next : '/');
    } catch (err) {
      setJoinError(
        err instanceof ApiError ? (ca.errors[err.code] ?? ca.errors.unknown) : ca.errors.unknown,
      );
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const signed = await signIn(handle, password, remember);
      if (signed.linkedNow) {
        navigate(`/u/${signed.handle ?? ''}?${signed.linkedNow.toLowerCase()}=linked`);
      } else if (next && !next.startsWith('/')) {
        window.location.assign(next);
      } else {
        navigate(next ?? '/');
      }
    } catch (err) {
      setError(
        err instanceof ApiError ? (ca.errors[err.code] ?? ca.errors.unknown) : ca.errors.unknown,
      );
    } finally {
      setBusy(false);
    }
  }

  const notice = pending
    ? `${ca.pendingNotice(steamNotice === 'pending' ? 'Steam' : 'Discord', who)}${unsure ? ` ${ca.membershipUnsure}` : ''}`
    : steamNotice && ca.steamNotices[steamNotice]
      ? ca.steamNotices[steamNotice]
      : discordNotice && ca.discordNotices[discordNotice]
        ? ca.discordNotices[discordNotice]
        : null;

  return (
    <main className="auth">
      <div className="wordmark" role="img" aria-label={meta.name}>
        <Wordmark name={meta.name} />
      </div>
      <section className="card form" aria-label={ca.signIn}>
        <h1>{ca.signIn}</h1>
        {notice && (
          <p className={pending ? 'hint' : 'error'} role="alert">
            {notice}
          </p>
        )}
        {offered && (
          <form
            className="steam-join"
            aria-label={offered === 'STEAM' ? ca.steamJoinTitle : ca.discordJoinTitle}
            onSubmit={join}
          >
            <h2>{offered === 'STEAM' ? ca.steamJoinTitle : ca.discordJoinTitle}</h2>
            <p className="muted small">
              {offered === 'STEAM' ? ca.steamJoinIntro(who) : ca.discordJoinIntro(who)}
            </p>
            <label>
              {ca.handle}
              <input
                name="joinHandle"
                autoComplete="username"
                autoCapitalize="none"
                required
                minLength={3}
                maxLength={32}
                pattern="[a-z0-9][a-z0-9._\-]{2,31}"
                value={joinHandle}
                onChange={(e) => setJoinHandle(e.target.value.toLowerCase())}
              />
              <span className="hint">{ca.handleHint}</span>
            </label>
            <label>
              {ca.displayName}
              <input
                name="joinDisplayName"
                autoComplete="nickname"
                maxLength={64}
                value={joinName}
                onChange={(e) => setJoinName(e.target.value)}
              />
            </label>
            <label>
              {ca.joinPassword}
              <input
                name="joinPassword"
                type="password"
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                value={joinPassword}
                onChange={(e) => setJoinPassword(e.target.value)}
              />
              <span className="hint">{ca.joinPasswordHint}</span>
            </label>
            {joinError && (
              <p className="error" role="alert">
                {joinError}
              </p>
            )}
            <button type="submit" className="primary" disabled={busy}>
              <i
                className={offered === 'STEAM' ? 'bi bi-steam' : 'bi bi-discord'}
                aria-hidden="true"
              />{' '}
              {ca.register}
            </button>
            <p className="hint">
              {offered === 'STEAM' ? ca.steamJoinHaveAccount : ca.discordJoinHaveAccount}
            </p>
          </form>
        )}
        {providers && (
          <div className="doors-equal">
            {meta.features.steam && (
              <ProviderButton
                provider="STEAM"
                href={steamStartUrl({ next: next ?? undefined })}
                label={ca.steamSignIn}
              />
            )}
            {meta.features.discord && (
              <ProviderButton
                provider="DISCORD"
                href={discordStartUrl({ next: next ?? undefined })}
                label={ca.discordSignIn}
              />
            )}
            <button
              type="button"
              className="provider password"
              aria-expanded={formShown}
              aria-controls="signin-password"
              onClick={() => setPasswordOpen(true)}
            >
              <i className="bi bi-person-lock" aria-hidden="true" />
              <span>{ca.passwordSignIn}</span>
            </button>
            <p className="muted small">{ca.signInDoorsNote}</p>
          </div>
        )}
        {formShown && (
          <form id="signin-password" onSubmit={submit} aria-label={ca.signIn}>
            <label>
              {ca.handle}
              <input
                name="handle"
                autoComplete="username"
                autoCapitalize="none"
                required
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
              />
            </label>
            <label>
              {ca.password}
              <input
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              {ca.remember}
            </label>
            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}
            <button type="submit" className="primary" disabled={busy}>
              {ca.signIn}
            </button>
            <p className="muted small">{ca.forgotPassword}</p>
          </form>
        )}
        <p className="muted small">
          {me.registrationOpen ? ca.firstAccountNote : ca.noAccount}{' '}
          <Link to={`/register${nextQuery(next)}`}>
            {me.registrationOpen ? ca.registerFirst : ca.register}
          </Link>
        </p>
      </section>
    </main>
  );
}
